import { Component, OnInit, QueryList, ViewChildren, Input, Optional } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, map, take, takeUntil, tap } from 'rxjs/operators';
import { areasSelector } from 'src/app/global/store/global.selectors';
import { State } from 'src/app/store';
import { BcitiCategoryInputComponent, CategoryInputState, CategoryOutput, PropertyLabelInput } from '../../forms/category/bciti-category-input.component';
import { GetAdminOrganisationAreas } from 'src/app/global/global.model';
import {
    CreateOrganisationAreaAction,
    CreateOrganisationAreaErrorAction,
    CreateOrganisationAreaSuccessAction,
    DeleteOrganisationAreaAction,
    DeleteOrganisationAreaErrorAction,
    DeleteOrganisationAreaSuccessAction,
    GetOrganisationAreasAction,
    GetOrganisationAreasErrorAction,
    GetOrganisationAreasSuccessAction,
    UpdateOrganisationAreaAction,
    UpdateOrganisationAreaErrorAction,
    UpdateOrganisationAreaSuccessAction,
    GetCollectionsAction,
} from 'src/app/global/store/global.actions';
import { Actions, ofType } from '@ngrx/effects';
import { AreaType } from '@bciti/types';

@Component({
    selector: 'app-collection-areas-dialog',
    templateUrl: './collection-areas-dialog.component.html',
    styleUrls: ['./collection-areas-dialog.component.scss'],
})
export class CollectionAreasDialogComponent implements OnInit {
    @Input() isCreatingOnly: boolean = false;

    destroy$ = new Subject();

    loading = false;
    creating = false;
    disableActions = false;
    toggleAddButton = true;
    propertyLabelInput = PropertyLabelInput;

    areas$ = this.store.select(areasSelector).pipe(takeUntil(this.destroy$));

    @ViewChildren(BcitiCategoryInputComponent) areaInputComponents: QueryList<BcitiCategoryInputComponent>;

    get isInline(): boolean {
        return !this.dialogRef;
    }

    constructor(
        @Optional() private dialogRef: MatDialogRef<CollectionAreasDialogComponent>,
        private store: Store<State>,
        private actions$: Actions,
    ) {}

    ngOnInit(): void {
        this.actions$
            .pipe(
                ofType(GetOrganisationAreasAction),
                tap(() => (this.loading = true)),
            )
            .subscribe();

        this.actions$
            .pipe(
                ofType(GetOrganisationAreasSuccessAction, GetOrganisationAreasErrorAction),
                tap(() => (this.loading = false)),
            )
            .subscribe();

        this.areas$
            .pipe(
                take(1),
                filter((areas) => areas.length === 0),
                map(() => this.store.dispatch(GetOrganisationAreasAction())),
            )
            .subscribe();
    }

    close(): void {
        this.dialogRef?.close();
    }

    closeAllOtherForms(targetArea: GetAdminOrganisationAreas): void {
        this.creating = false;
        this.areaInputComponents.forEach((input) => {
            // TODO: review condition when data interface is confirmed
            if (targetArea?.name !== input?.category?.name) {
                input.state = CategoryInputState.DISPLAYING;
            }
        });
    }

    startCreating(): void {
        this.creating = true;
        this.toggleAddButtonDisplay();
        this.areaInputComponents.forEach((input) => {
            if (input?.category?.name) {
                input.state = CategoryInputState.DISPLAYING;
            }
        });
    }

    handleCancelCreate(): void {
        this.creating = false;
        this.toggleAddButton = true;
    }

    toggleAddButtonDisplay($event?) {
        return (this.toggleAddButton = $event === CategoryInputState.DISPLAYING || !this.creating);
    }

    handleSave(e: CategoryOutput) {
        // all other inputs should be disabled / cannot be opened, buttons should be disabled except to close the dialog
        const statusInputRef = this.areaInputComponents.find((input) => input.id === e.inputId);
        if (e.category._id) {
            // patch
            this.updateArea(e, statusInputRef);
        } else {
            // create
            this.createArea(e, statusInputRef);
        }
    }

    updateArea(e: CategoryOutput, inputRef: BcitiCategoryInputComponent): void {
        this.disableActions = true;
        inputRef.loading = true;

        const payload = { ...e.category, type: AreaType.DISTRICT };

        this.store.dispatch(UpdateOrganisationAreaAction({ area: payload as GetAdminOrganisationAreas }));

        this.actions$
            .pipe(
                ofType(UpdateOrganisationAreaSuccessAction, UpdateOrganisationAreaErrorAction),
                take(1),
                tap(() => {
                    inputRef.loading = false;
                    inputRef.state = CategoryInputState.DISPLAYING;
                    this.disableActions = false;
                    this.toggleAddButton = true;
                    this.store.dispatch(GetCollectionsAction());
                }),
            )
            .subscribe();
    }

    createArea(e: CategoryOutput, inputRef: BcitiCategoryInputComponent): void {
        this.disableActions = true;
        inputRef.loading = true;

        this.store.dispatch(CreateOrganisationAreaAction({ area: e.category as GetAdminOrganisationAreas }));

        this.actions$
            .pipe(
                ofType(CreateOrganisationAreaSuccessAction, CreateOrganisationAreaErrorAction),
                take(1),
                tap(() => {
                    inputRef.loading = false;
                    inputRef.state = CategoryInputState.DISPLAYING;
                    this.disableActions = false;
                    this.handleCancelCreate();
                    this.toggleAddButton = true;
                }),
            )
            .subscribe();
    }

    deleteArea(e: CategoryOutput): void {
        this.areas$.pipe(take(1)).subscribe((areas) => {
            if (areas.length === 1) {
                return;
            }
            const areaInputRef = this.areaInputComponents.find((input) => input.id === e.inputId);
            this.disableActions = true;
            areaInputRef.loading = true;

            this.store.dispatch(DeleteOrganisationAreaAction({ area: e.category as GetAdminOrganisationAreas }));

            this.actions$
                .pipe(
                    ofType(DeleteOrganisationAreaSuccessAction),
                    take(1),
                    tap(() => {
                        areaInputRef.loading = false;
                        areaInputRef.state = CategoryInputState.DISPLAYING;
                        this.disableActions = false;
                        this.toggleAddButton = true;
                        this.store.dispatch(GetCollectionsAction());
                    }),
                )
                .subscribe();

            this.actions$
                .pipe(
                    ofType(DeleteOrganisationAreaErrorAction),
                    take(1),
                    tap((response) => {
                        if (response.error.status === 400) {
                            areaInputRef.serviceToDeleteFirst = response.error.error.data;
                        }
                        areaInputRef.loading = false;
                        this.disableActions = false;
                        this.toggleAddButton = true;
                    }),
                )
                .subscribe();
        });
    }

    handleCancelCreatingOnly() {
        this.handleCancelCreate();
        this.close();
    }

    handleSaveCreateOnly(e: CategoryOutput) {
        const inputRef = this.areaInputComponents.find((input) => input.id === e.inputId);
        this.disableActions = true;
        inputRef.loading = true;
        this.store.dispatch(CreateOrganisationAreaAction({ area: e.category as GetAdminOrganisationAreas }));
        this.actions$
            .pipe(
                ofType(CreateOrganisationAreaSuccessAction, CreateOrganisationAreaErrorAction),
                take(1),
                tap(() => {
                    inputRef.loading = false;
                    this.disableActions = false;
                    this.handleCancelCreate();
                    this.close();
                }),
            )
            .subscribe();
    }
}
